import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Card, { CardBody, CardHeader, CardFooter } from '../components/Card';
import Button from '../components/Button';
import { ArrowLeftIcon, CreditCardIcon, LockIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { services } from '../data/services';
import { mdas } from '../data/mdas';
const PaymentPage: React.FC = () => {
  const {
    serviceId
  } = useParams<{
    serviceId: string;
  }>();
  const navigate = useNavigate();
  const [service, setService] = useState(services.find(s => s.id === serviceId));
  const [mda, setMda] = useState(service ? mdas.find(m => m.id === service.mdaId) : null);
  // Payment form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    // Find service and MDA based on serviceId
    const currentService = services.find(s => s.id === serviceId);
    setService(currentService);
    if (currentService) {
      const currentMda = mdas.find(m => m.id === currentService.mdaId);
      setMda(currentMda || null);
    }
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [serviceId]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Basic validation
    if (!cardNumber || !cardName || !expiryDate || !cvv) {
      setError('All fields are required');
      return;
    }
    if (cardNumber.replace(/\s/g, '').length !== 16) {
      setError('Card number must be 16 digits');
      return;
    }
    if (cvv.length !== 3) {
      setError('CVV must be 3 digits');
      return;
    }
    // Process payment
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      // Generate a random transaction ID
      const transactionId = Math.random().toString(36).substring(2, 15);
      navigate(`/confirmation/${transactionId}`);
    }, 2000);
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  if (!service || !mda) {
    return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-5890">
        <Header data-id="element-5891" />
        <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8" data-id="element-5892">
          <div className="max-w-7xl mx-auto text-center py-12" data-id="element-5893">
            <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-5894">
              Service Not Found
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-5895">
              The service you're trying to pay for doesn't exist.
            </p>
            <Link to="/mdas" data-id="element-5896">
              <Button variant="primary" data-id="element-5897">Browse Services</Button>
            </Link>
          </div>
        </main>
        <Footer data-id="element-5898" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-5899">
      <Header data-id="element-5900" />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8" data-id="element-5901">
        <div className="max-w-3xl mx-auto" data-id="element-5902">
          {/* Page Header */}
          <div className="mb-8" data-id="element-5903">
            <Link to={`/services/${service.id}`} className="inline-flex items-center text-green-600 hover:text-green-700 mb-4" data-id="element-5904">
              <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-5905" />
              Back to Service Details
            </Link>
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5906">
              Payment for {service.name}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-5907">
              Complete your payment to proceed with your application
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5908">
            {/* Payment Form */}
            <div className="md:col-span-2" data-id="element-5909">
              <Card data-id="element-5910">
                <CardHeader data-id="element-5911">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-5912">
                    Payment Details
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-5913">
                    Enter your card information to complete the payment
                  </p>
                </CardHeader>
                <CardBody data-id="element-5914">
                  {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center" data-id="element-5915">
                      <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-5916" />
                      <span data-id="element-5917">{error}</span>
                    </div>}
                  <form onSubmit={handleSubmit} data-id="element-5918">
                    <div className="space-y-4" data-id="element-5919">
                      <div data-id="element-5920">
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5921">
                          Card Number
                        </label>
                        <div className="relative rounded-md shadow-sm" data-id="element-5922">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5923">
                            <CreditCardIcon className="h-5 w-5 text-gray-400" data-id="element-5924" />
                          </div>
                          <input type="text" id="cardNumber" placeholder="1234 5678 9012 3456" value={cardNumber} onChange={e => setCardNumber(formatCardNumber(e.target.value))} maxLength={19} className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-5925" />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-5926">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6" data-id="element-5927" />
                          </div>
                        </div>
                      </div>
                      <div data-id="element-5928">
                        <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5929">
                          Cardholder Name
                        </label>
                        <input type="text" id="cardName" placeholder="John Doe" value={cardName} onChange={e => setCardName(e.target.value)} className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-5930" />
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-5931">
                        <div data-id="element-5932">
                          <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5933">
                            Expiry Date
                          </label>
                          <input type="text" id="expiryDate" placeholder="MM/YY" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} maxLength={5} className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-5934" />
                        </div>
                        <div data-id="element-5935">
                          <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5936">
                            CVV
                          </label>
                          <div className="relative rounded-md shadow-sm" data-id="element-5937">
                            <input type="text" id="cvv" placeholder="123" value={cvv} onChange={e => setCvv(e.target.value.replace(/\D/g, ''))} maxLength={3} className="focus:ring-green-500 focus:border-green-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md" data-id="element-5938" />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-5939">
                              <LockIcon className="h-4 w-4 text-gray-400" data-id="element-5940" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6" data-id="element-5941">
                      <Button type="submit" variant="primary" fullWidth disabled={isProcessing} data-id="element-5942">
                        {isProcessing ? 'Processing Payment...' : `Pay ₦${service.fee.toLocaleString()}`}
                      </Button>
                    </div>
                  </form>
                  <div className="mt-4 flex items-center justify-center" data-id="element-5943">
                    <LockIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-5944" />
                    <span className="text-xs text-gray-500" data-id="element-5945">
                      Secure payment processed by Paystack
                    </span>
                  </div>
                </CardBody>
              </Card>
            </div>
            {/* Order Summary */}
            <div className="md:col-span-1" data-id="element-5946">
              <Card data-id="element-5947">
                <CardHeader data-id="element-5948">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-5949">
                    Order Summary
                  </h2>
                </CardHeader>
                <CardBody className="p-4" data-id="element-5950">
                  <div className="space-y-4" data-id="element-5951">
                    <div data-id="element-5952">
                      <p className="text-sm text-gray-500" data-id="element-5953">Service</p>
                      <p className="font-medium" data-id="element-5954">{service.name}</p>
                    </div>
                    <div data-id="element-5955">
                      <p className="text-sm text-gray-500" data-id="element-5956">Provider</p>
                      <p className="font-medium" data-id="element-5957">{mda.name}</p>
                    </div>
                    <div data-id="element-5958">
                      <p className="text-sm text-gray-500" data-id="element-5959">Processing Time</p>
                      <p className="font-medium" data-id="element-5960">{service.processingTime}</p>
                    </div>
                  </div>
                </CardBody>
                <CardFooter className="bg-gray-50" data-id="element-5961">
                  <div className="flex justify-between items-center" data-id="element-5962">
                    <span className="font-medium" data-id="element-5963">Total</span>
                    <span className="text-xl font-bold text-gray-900" data-id="element-5964">
                      ₦{service.fee.toLocaleString()}
                    </span>
                  </div>
                </CardFooter>
              </Card>
              <div className="mt-4 bg-green-50 rounded-lg p-4" data-id="element-5965">
                <div className="flex" data-id="element-5966">
                  <div className="flex-shrink-0" data-id="element-5967">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-5968" />
                  </div>
                  <div className="ml-3" data-id="element-5969">
                    <h3 className="text-sm font-medium text-green-800" data-id="element-5970">
                      Secure Payment
                    </h3>
                    <div className="mt-2 text-sm text-green-700" data-id="element-5971">
                      <p data-id="element-5972">
                        Your payment information is encrypted and secure. We do
                        not store your card details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5973" />
    </div>;
};
export default PaymentPage;